/**
 * KineticBand — a full-bleed band of giant hollow display type that scrolls
 * sideways forever, tilted a couple of degrees. Hovering a phrase fills it
 * with the edge-line green; hovering the band holds it still. Purely
 * decorative (hidden from assistive tech: the same facts are on the page in
 * plain text). Web only. Reduced motion: a still, centered row.
 *
 *   <KineticBand items={['Fair odds', 'Expected value', 'Best book']} />
 *   <KineticBand items={...} reverse tilt={1.5} faint />
 */
import React, { useEffect, useRef } from 'react';
import { View, Text, Platform } from 'react-native';
import { IS_WEB, useReducedMotion } from '../../utils/motion';
import injectCss from '../../utils/injectCss';

const CSS = `
[data-kband] { overflow: hidden; width: 100%; padding: 14px 0; -webkit-mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent); mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent); }
[data-kband-row] { display: flex; flex-direction: row; width: max-content; animation: boKband var(--bo-kb-dur, 38s) linear infinite; }
[data-kband-row="reverse"] { animation-direction: reverse; }
@media (hover: hover) and (pointer: fine) { [data-kband]:hover [data-kband-row] { animation-play-state: paused; } [data-kband-item]:hover { color: #34D399; -webkit-text-stroke-color: #34D399; } }
[data-kband-item] { color: transparent; -webkit-text-stroke: 1.5px rgba(255,255,255,0.32); transition: color 380ms cubic-bezier(0.2,0,0,1), -webkit-text-stroke-color 380ms; white-space: nowrap; }
[data-kband="faint"] [data-kband-item] { -webkit-text-stroke-color: rgba(255,255,255,0.14); }
[data-kband-star] { color: #8B5CF6; }
@keyframes boKband { to { transform: translate3d(-50%, 0, 0); } }
@media (prefers-reduced-motion: reduce) { [data-kband-row] { animation: none; width: auto; flex-wrap: wrap; justify-content: center; } [data-kband-dup] { display: none; } }
`;

const DISPLAY = 'ClimateCrisis';

export default function KineticBand({ items = [], reverse = false, tilt = -2, faint = false, size = 64, duration = 38, style }) {
    const reduced = useReducedMotion();
    const rowRef = useRef(null);
    useEffect(() => { rowRef.current?.style?.setProperty('--bo-kb-dur', `${duration}s`); }, [duration]);
    if (!IS_WEB || !items.length) return null;
    injectCss('kinetic-band', CSS);
    const run = reduced ? items : [...items, ...items];
    return (
        <View
            style={[{ transform: [{ rotate: `${tilt}deg` }] }, style]}
            dataSet={{ kband: faint ? 'faint' : 'on' }}
            aria-hidden
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
        >
            <View ref={rowRef} dataSet={{ kbandRow: reverse ? 'reverse' : 'forward' }}>
                {run.map((t, i) => (
                    <View key={`${t}-${i}`} style={{ flexDirection: 'row', alignItems: 'center' }} {...(i >= items.length ? { dataSet: { kbandDup: '' } } : {})}>
                        <Text style={{ fontFamily: DISPLAY, fontSize: size, lineHeight: size * 1.1, textTransform: 'uppercase', paddingHorizontal: size * 0.35, ...(Platform.OS === 'web' ? { cursor: 'default' } : {}) }} dataSet={{ kbandItem: '' }}>{t}</Text>
                        <Text style={{ fontSize: size * 0.4 }} dataSet={{ kbandStar: '' }}>✦</Text>
                    </View>
                ))}
            </View>
        </View>
    );
}
